// src/pages/admin/AdminRepairs.js
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import AdminSidebar from './components/AdminSidebar';
import RepairForm from './components/RepairForm';
import '../../styles/admin.css';

function AdminRepairs() {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get deviceId from query params
  const queryParams = new URLSearchParams(location.search);
  const deviceIdFromQuery = queryParams.get('deviceId');
  
  const [devices, setDevices] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState(deviceIdFromQuery || '');
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [repairs, setRepairs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingRepair, setEditingRepair] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  // Fetch all devices on mount
  useEffect(() => {
    fetchDevices();
  }, []);
  
  // When selectedDeviceId changes, fetch repairs for that device
  useEffect(() => {
    if (selectedDeviceId) {
      const device = devices.find(d => d._id === selectedDeviceId);
      setSelectedDevice(device);
      if (device) {
        fetchRepairs(selectedDeviceId);
      }
    } else {
      setSelectedDevice(null);
      setRepairs([]);
    }
  }, [selectedDeviceId, devices]);
  
  const fetchDevices = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        navigate('/admin/login');
        return;
      }
      
      const response = await fetch('/api/admin/devices', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch devices');
      }
      
      const data = await response.json();
      setDevices(data);
    } catch (err) {
      console.error('Error fetching devices:', err);
      setError('Failed to load devices');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchRepairs = async (deviceId) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`/api/admin/devices/${deviceId}/repairs`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch repairs');
      }
      
      const data = await response.json();
      setRepairs(data);
      setError('');
    } catch (err) {
      console.error('Error fetching repairs:', err);
      setError('Failed to load repairs');
      setRepairs([]);
    }
  };
  
  const handleAddRepair = () => {
    setEditingRepair(null);
    setShowForm(true);
  };
  
  const handleEditRepair = (repair) => {
    setEditingRepair(repair);
    setShowForm(true);
  };
  
  const handleDeleteRepair = (repair) => {
    setEditingRepair(repair);
    setShowDeleteConfirm(true);
  };
  
  const confirmDeleteRepair = async () => {
    if (!editingRepair) return;
    
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`/api/admin/repairs/${editingRepair._id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete repair');
      }
      
      // Refresh repairs
      fetchRepairs(selectedDeviceId);
      setSuccess('Repair deleted successfully');
      setTimeout(() => setSuccess(''), 3000);
      setShowDeleteConfirm(false);
      setEditingRepair(null);
    } catch (err) {
      console.error('Error deleting repair:', err);
      setError('Failed to delete repair');
    }
  };
  
  const handleFormSubmit = async (repairData) => {
    try {
      const token = localStorage.getItem('adminToken');

      // Simple submission - all repairs are handled the same way now
      const url = editingRepair
        ? `/api/admin/repairs/${editingRepair._id}`
        : '/api/admin/repairs';
      const method = editingRepair ? 'PUT' : 'POST';

      const body = editingRepair
        ? repairData
        : { ...repairData, deviceId: selectedDeviceId };

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        throw new Error(`Failed to ${editingRepair ? 'update' : 'create'} repair`);
      }

      // Refresh repairs
      fetchRepairs(selectedDeviceId);
      setSuccess(`Repair ${editingRepair ? 'updated' : 'added'} successfully`);
      setTimeout(() => setSuccess(''), 3000);
      setShowForm(false);
      setEditingRepair(null);
    } catch (err) {
      console.error('Error saving repair:', err);
      setError(`Failed to ${editingRepair ? 'update' : 'create'} repair`);
    }
  };
  
  return (
    <div className="admin-layout">
      <AdminSidebar activeItem="repairs" />
      
      <div className="admin-content">
        <div className="section-header">
          <h1>Manage Repairs</h1>
          <Link to="/admin/devices" className="btn-admin btn-admin-secondary">
            Back to Devices
          </Link>
        </div>
        
        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}
        
        {loading ? (
          <div className="loading">Loading...</div>
        ) : (
          <>
            <div className="device-selector">
              <label htmlFor="device-select">Select Device:</label>
              <select
                id="device-select"
                value={selectedDeviceId}
                onChange={(e) => setSelectedDeviceId(e.target.value)}
                className="device-select"
              >
                <option value="">-- Select a device --</option>
                {devices.map(device => (
                  <option key={device._id} value={device._id}>
                    {device.brand} {device.model} ({device.type})
                  </option>
                ))}
              </select>
            </div>
            
            {selectedDevice && (
              <div className="device-info">
                <h3>Device: {selectedDevice.brand} {selectedDevice.model}</h3>
                <p>Type: {selectedDevice.type}</p>
                <p>Total Repairs: {repairs.length}</p>
              </div>
            )}
            
            {selectedDeviceId && (
              <>
                <div className="repairs-header">
                  <h2>Repairs</h2>
                  <button 
                    onClick={handleAddRepair}
                    className="btn-admin btn-admin-primary"
                  >
                    Add Repair
                  </button>
                </div>
                
                {repairs.length === 0 ? (
                  <div className="no-data-message">
                    <p>No repairs found for this device.</p>
                  </div>
                ) : (
                  <div className="repairs-list">
                    <table>
                      <thead>
                        <tr>
                          <th>Repair Type</th>
                          <th>Price</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {repairs.map(repair => (
                          <tr key={repair._id}>
                            <td>{repair.repair}</td>
                            <td>£{String(repair.price).replace(/[£$]/g, '')}</td>
                            <td className="actions">
                              <button 
                                onClick={() => handleEditRepair(repair)}
                                className="edit-button"
                              >
                                Edit
                              </button>
                              <button 
                                onClick={() => handleDeleteRepair(repair)}
                                className="delete-button"
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </>
        )}
        
        {showForm && (
          <RepairForm
            repair={editingRepair}
            onSubmit={handleFormSubmit}
            onCancel={() => {
              setShowForm(false);
              setEditingRepair(null);
            }}
          />
        )}
        
        {showDeleteConfirm && editingRepair && (
          <div className="delete-confirmation-modal">
            <div className="modal-content">
              <h3>Confirm Delete</h3>
              <p>Are you sure you want to delete "{editingRepair.repair}"?</p>
              
              <div className="modal-actions">
                <button 
                  onClick={() => setShowDeleteConfirm(false)}
                  className="cancel-button"
                >
                  Cancel
                </button>
                <button 
                  onClick={confirmDeleteRepair}
                  className="delete-button"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminRepairs;